import React from 'react';
import { fetchWrapper } from '../../APIManager';
const UserStateContext = /*#__PURE__*/React.createContext();
const UserDispatchContext = /*#__PURE__*/React.createContext();
const initialState = {};

/** REDUCER **/
function UserReducer(state, action) {
  switch (action.type) {
    case 'GET_INITIAL':
      {
        return initialState;
      }
    default:
      {
        throw new Error('Unhandled action type caught');
      }
  }
}

/** PROVIDER **/
function UserProvider(_ref) {
  let {
    value,
    children
  } = _ref;
  const [state, dispatch] = React.useReducer(UserReducer, initialState);
  return /*#__PURE__*/React.createElement(UserStateContext.Provider, {
    value: state
  }, /*#__PURE__*/React.createElement(UserDispatchContext.Provider, {
    value: dispatch
  }, children));
}

/** STATE/DISPATCH HOOKS **/
function useUserState() {
  const context = React.useContext(UserStateContext);
  if (context === undefined) {
    throw new Error('useUserState must be used within a UserProvider');
  }
  return context;
}
function useUserDispatch() {
  const context = React.useContext(UserDispatchContext);
  if (context === undefined) {
    throw new Error('useUserDispatch must be used within a UserProvider');
  }
  return context;
}

/** API **/
const userAPI = {
  getUser: async (url, authUserName, apiKey) => {
    try {
      const slug = `/u/${authUserName}.json`;
      const response = await fetchWrapper(url, slug, apiKey);
      if (!response) {
        throw new Error('Could not get user.');
      }
      return response;
    } catch (err) {
      return {};
    }
  },
  putUser: async (url, authUserName, data, api_key) => {
    try {
      const slug = `/u/${authUserName}.json`;
      const response = await fetchWrapper(url, slug, api_key, 'PUT', data);
      if (!response || response.success !== 'OK') {
        throw new Error('Error occurred while updating user.');
      }
      return response;
    } catch (err) {
      return {};
    }
  },
  getUserUnsubscribe: async (url, unsubscribeKey, apiKey) => {
    try {
      const slug = `/email/unsubscribe/${unsubscribeKey}`;
      const response = await fetchWrapper(url, slug, apiKey);
      if (!response) {
        throw new Error('Could not get user unsubscribe options.');
      }
      return response;
    } catch (err) {
      return {};
    }
  },
  postUserUnsubscribe: async (url, unsubscribeKey, data, apiKey) => {
    try {
      const slug = `/email/unsubscribe/${unsubscribeKey}`;
      const response = await fetchWrapper(url, slug, apiKey, 'POST', {
        ...data,
        override_redirect: true
      });
      if (!response || !['OK', true].includes(response.success)) {
        throw new Error('Error occurred while updating status.');
      }
      return response;
    } catch (err) {
      return {};
    }
  },
  /* TODO: Probably don't need most of the following API calls
   * Revisit when we implement PMs */
  getGroupMembers: async (url, apiKey, groupName) => {
    try {
      const slug = `/groups/${groupName}/members.json`;
      const response = await fetchWrapper(url, slug, apiKey);
      if (response === null || response === undefined) {
        throw new Error('Could not get members');
      }
      return response;
    } catch (err) {
      return [];
    }
  },
  getSingleGroup: async (url, apiKey, groupName) => {
    try {
      const slug = `/groups/${groupName}`;
      const response = await fetchWrapper(url, slug, apiKey);
      if (!response) {
        throw new Error('Could not get group data');
      }
      const {
        group
      } = response;
      return group;
    } catch (err) {
      return {};
    }
  },
  addToPMOptedOutGroup: async (url, apiKey, pmOptedOutGroupID) => {
    /**
     * The group setting should allow users to join freely.
     * Related key:value pair: {public_admission: true}
     */
    try {
      const slug = `/groups/${pmOptedOutGroupID}/join.json`;
      await fetchWrapper(url, slug, apiKey, 'PUT');
      return true;
    } catch (err) {
      console.log('Error while adding to group');
      return false;
    }
  },
  removeFromPMOptedOutGroup: async (url, apiKey, pmOptedOutGroupID) => {
    /**
     * The group setting should allow users to leave freely.
     * Related key:value pair: {public_exit: true}
     */
    try {
      const slug = `/groups/${pmOptedOutGroupID}/leave.json`;
      await fetchWrapper(url, slug, apiKey, 'DELETE');
      return true;
    } catch (err) {
      console.log('Error while removing from group');
      return false;
    }
  },
  checkIfImInTheGroup: async (url, apiKey, groupName) => {
    try {
      const slug = `/groups/${groupName}`;
      const response = await fetchWrapper(url, slug, apiKey);
      if (!response) {
        throw new Error('Could not get the requested information.');
      }
      const data = response === null || response === void 0 ? void 0 : response.is_group_user;
      return data;
    } catch (err) {
      return {};
    }
  },
  adminAddToPMOptedOutGroup: async (url, apiKey, pmOptedOutGroupID, authUserName) => {
    try {
      const slug = `/groups/${pmOptedOutGroupID}/members.json`;
      const response = await fetchWrapper(url, slug, apiKey, 'PUT', {
        usernames: authUserName
      });
      if (!response) {
        throw new Error('Failed to add to PM_opted_out group.');
      }
      return response;
    } catch (err) {
      return {};
    }
  },
  adminRemoveFromPMOptedOutGroup: async (url, apiKey, pmOptedOutGroupID, authUserName) => {
    try {
      const slug = `/groups/${pmOptedOutGroupID}/members.json`;
      const response = await fetchWrapper(url, slug, apiKey, 'DELETE', {
        usernames: authUserName
      });
      if (!response) {
        throw new Error('Failed to remove from PM_opted_out group.');
      }
      return response;
    } catch (err) {
      return {};
    }
  },
  getAllUsers: async (url, apiKey) => {
    try {
      const period = 'all'; // required, 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly'
      const order = 'days_visited'; // required, 'liked_received' | 'likes_given' | 'topic_count' | 'post_count' | 'posts_read'
      const slug = `/directory_items.json?period=${period}&order=${order}`;
      const response = await fetchWrapper(url, slug, apiKey);
      if (!response || !response.directory_items.length) {
        throw new Error('Failed to get users list or list is empty.');
      }
      /*
      response.meta: {
        last_updated_at: timestamp
        total_rows_directory_items: number of entire data. currently paginates by 50
        load_more_directory_items: slug for next api call
      }
      */
      return response;
    } catch (err) {
      return [];
    }
  }
};
export { UserProvider, useUserState, useUserDispatch, userAPI };
//# sourceMappingURL=index.js.map